import { createContext, useContext } from 'react';
import type { FormikProps } from 'formik';
import {
  DetailsForm,
  Option,
  StaffEntry,
  PriorityEntry,
  PriorityRemoveEntry,
  SeniorityEntry,
  TableGridEntry,
  OrderTemplate,
} from './types';
import { ResolvedOptions } from './planningOptions';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Cross-step state for the Plan New Counselling wizard. Replaces the ~40
 * mutable class fields in the Angular component. Provided by the shell
 * (PlanNewCounselling.tsx) and consumed by each step component, so we avoid
 * prop-drilling the whole bag through every step.
 */
export interface PlanningWizardValue {
  // role
  userType: string;
  userType1: string;
  isSCERT: boolean;
  isEdit: boolean;
  counsellingId: string;

  // step 1 — details form (Formik)
  detailsForm: FormikProps<DetailsForm>;
  options: ResolvedOptions;
  locationDisable: boolean;
  setLocationDisable: (v: boolean) => void;

  // master data (from JSON queries, key already resolved per role/directorate)
  categoryList: any[];
  managementList: any[];
  panelList: any[];
  staffTypeList: Option[];
  subjectList: Option[];
  allDirectorate: Option[];
  orderList: SeniorityEntry[];
  setOrderList: React.Dispatch<React.SetStateAction<SeniorityEntry[]>>;
  allOrderList: SeniorityEntry[];

  // step 2 — staff
  staffList: StaffEntry[];
  setStaffList: React.Dispatch<React.SetStateAction<StaffEntry[]>>;
  staffType: string;
  setStaffType: (v: string) => void;
  subject: string;
  setSubject: (v: string) => void;
  isShowSubject: boolean;
  staffDisable: boolean;
  setStaffDisable: (v: boolean) => void;

  // step 2/3 — school type + minority
  schType: string;
  setSchType: (v: string) => void;
  counsellingMinority: string;
  setCounsellingMinority: (v: string) => void;
  vacancyMinority: string;
  setVacancyMinority: (v: string) => void;

  // step 3 — school category/management/panel
  selectedSchCategory: string[];
  setSelectedSchCategory: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSchManage: string[];
  setSelectedSchManage: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSchPanel: string[];
  setSelectedSchPanel: React.Dispatch<React.SetStateAction<string[]>>;

  // step 4 — vacancy category/management/panel
  vacUpdatedBy: string;
  setVacUpdatedBy: (v: string) => void;
  selectedVacCategory: string[];
  setSelectedVacCategory: React.Dispatch<React.SetStateAction<string[]>>;
  selectedVacManage: string[];
  setSelectedVacManage: React.Dispatch<React.SetStateAction<string[]>>;
  selectedVacPanel: string[];
  setSelectedVacPanel: React.Dispatch<React.SetStateAction<string[]>>;

  // step 5 — approval
  level1Approver: string;
  setLevel1Approver: (v: string) => void;
  level2Approver: string;
  setLevel2Approver: (v: string) => void;

  // step 6 — priority
  priorityList: Option[];
  setPriorityList: React.Dispatch<React.SetStateAction<Option[]>>;
  allPriorityList: Option[];
  addedPriorityList: PriorityEntry[];
  setAddedPriorityList: React.Dispatch<React.SetStateAction<PriorityEntry[]>>;
  removeList: PriorityRemoveEntry[];
  setRemoveList: React.Dispatch<React.SetStateAction<PriorityRemoveEntry[]>>;
  refreshPriorityMaster: () => void;

  // step 7 — table grid
  tableValueList: Option[];
  setTableValueList: React.Dispatch<React.SetStateAction<Option[]>>;
  allTableValueList: Option[];
  addedTableGridList: TableGridEntry[];
  setAddedTableGridList: React.Dispatch<React.SetStateAction<TableGridEntry[]>>;

  // step 8 — order template
  orderTemplate: FormikProps<OrderTemplate>;
  orderArray: OrderTemplate[];
  setOrderArray: React.Dispatch<React.SetStateAction<OrderTemplate[]>>;
  loadOrderTemplate: (type: number) => void;

  // step 9 — seniority
  addedSeniorityList: SeniorityEntry[];
  setAddedSeniorityList: React.Dispatch<React.SetStateAction<SeniorityEntry[]>>;

  // navigation
  activeIndex: number;
  nextPage: () => void;
  previousPage: () => void;
  goToStep: (i: number) => void;

  // submit
  finalSubmit: () => void;
  confirmationDetails: () => void;
}

export const PlanningWizardContext = createContext<PlanningWizardValue | null>(
  null
);

export function usePlanningWizard(): PlanningWizardValue {
  const ctx = useContext(PlanningWizardContext);
  if (!ctx) {
    throw new Error(
      'usePlanningWizard must be used within a PlanningWizardContext provider'
    );
  }
  return ctx;
}
